<template>
  <TimeoutModal />
  <SystemAlertReviewDialog
    :is-open="SystemAlertsClientStore.isSystemAlertDialogShowing"
    @update:is-open="
      (value) => (SystemAlertsClientStore.isSystemAlertDialogShowing = value)
    "
  />
  <SystemAlertViewDialog
    v-model="SystemAlertsClientStore.isSystemAlertViewModalOpen"
    :alert="SystemAlertsClientStore.alertDetailsForUser"
  />
  <LogoLoaderSpinner
    v-if="showGlobalAppLoadingState"
    loading-message="Loading..."
  />
  <router-view v-else />

  <div
    id="global-state-message"
    class="sr-only"
    aria-live="assertive"
    aria-atomic="true"
  >
    {{ globalStateMessage }}
  </div>
</template>

<script lang="ts" setup>
import { useSSOStore } from './stores/sso-configuration'
import { getSubdomain } from './helpers/api'
import { stop } from '~/composables/signalR/useSignalR'
import { useReportExport } from '~/composables/signalR/useReportExport'
import { AuthStatus, useAuthStore } from '~/stores/auth'
import { useSystemAlertsClientStore } from '~/stores/systemAlertsClient'
import { useNotificationsStore } from '~/stores/notifications'
import { useNewNotificationReceived } from '~/composables/signalR/useNewNotificationReceived'
import { useProjectExport } from './composables/signalR/useProjectExport'
import { usePageTitle } from './composables/usePageTitle'
import { useFixedQuasarScroll } from './composables/useFixedQuasarScroll'
import { useExecutiveSummary } from './composables/signalR/useExecutiveSummary'

const NotificationsStore = useNotificationsStore()
const SystemAlertsClientStore = useSystemAlertsClientStore()
const { authStatus, showGlobalAppLoadingState, isViewAsUser, user } =
  storeToRefs(useAuthStore())
const SSOStore = useSSOStore()

const newNotificationReceived = useNewNotificationReceived()
const reportExport = useReportExport()
const projectExport = useProjectExport()
const executiveSummaryGeneration = useExecutiveSummary()

usePageTitle()
useFixedQuasarScroll()

const handleLoggedInUpdates = async () => {
  newNotificationReceived.handleOn()
  await reportExport.handleOn()
  executiveSummaryGeneration.handleOn()
  if (!isViewAsUser.value) {
    await SystemAlertsClientStore.handleSystemAlertReviewModal()
  }
}

const handleLoggedOutUpdates = async () => {
  newNotificationReceived.handleOff()
  reportExport.handleOff()
  projectExport.handleOff()
  executiveSummaryGeneration.handleOff()

  await stop()
  NotificationsStore.dropdownNotifications = []
  NotificationsStore.allNotifications = []
}

watch(authStatus, async (status) => {
  if (status === AuthStatus.LoggedIn) {
    await handleLoggedInUpdates()
  } else if (status === AuthStatus.InitiatingLoggingOut) {
    handleLoggedOutUpdates()
  }
})

const handleGetSSOConfig = () => {
  if (getSubdomain()) {
    SSOStore.getUnelevatedSSOData()
  }
}

onMounted(() => {
  handleGetSSOConfig()
})

onBeforeUnmount(() => {
  handleLoggedOutUpdates()
})

const globalStateMessage = ref('')
watch([showGlobalAppLoadingState, user], async (newValues, oldValues) => {
  let newMessage = ''

  if (newValues[0] && !oldValues[0]) {
    newMessage += 'Berkshire Client Portal is loading.'
  } else if (!newValues[0] && oldValues[0]) {
    newMessage += 'Berkshire Client Portal loaded.'
  }

  if (
    newValues[1]?.hasUnviewedNotifications &&
    !oldValues[1]?.hasUnviewedNotifications
  ) {
    newMessage += ' New notifications are available.'
  }

  globalStateMessage.value = newMessage

  setTimeout(() => {
    globalStateMessage.value = ''
  }, 5000)
})
</script>
